/**
 * Debug flight HUD — a text panel, until the milestone that draws real instruments.
 *
 * Everything on it is what a pilot would have in front of them plus the two numbers a flight
 * model needs on display while it is being tuned (angle of attack and load factor). Aviation
 * units throughout, converted here at the UI boundary: the simulation itself is SI everywhere.
 *
 * Same cost discipline as the performance overlay: the DOM is built once, only `textContent`
 * changes, and it refreshes at 10 Hz rather than every frame — fast enough to read a rate of
 * change, slow enough to be legible.
 *
 * `updateFrame(alpha)`: yes, self-throttled.
 */

import { MIN_MEANINGFUL_AIRSPEED_MPS, type FlightState } from '../sim/fdm.js';
import { RAD2DEG } from '../core/math.js';
import { metresToFeet, mpsToFpm, mpsToKnots } from '../core/units.js';

const REFRESH_HZ = 10;

/**
 * The rows that survive {@link FlightHud.setCompact} — what a pilot flies by, and nothing else.
 *
 * The full panel is twenty rows and about 330 px tall, which is fine down the side of a desktop
 * window and is the entire height of a phone in landscape. Measured on an 844×390 viewport it
 * covered the throttle lever and all six buttons completely, so the touch tier was unflyable for a
 * reason that had nothing to do with the touch tier.
 *
 * Chosen by asking what is *unavailable elsewhere*. Attitude is on the horizon in front of you and
 * throttle is written on the lever you are holding; airspeed, altitude, height above the ground,
 * vertical speed and heading are not anywhere else, and the status line is how you learn you have
 * stalled. Angle of attack, load factor and the three trims are instruments for tuning a flight
 * model, which is not what anybody is doing on a phone.
 */
const COMPACT_ROWS: ReadonlySet<string> = new Set(['ias', 'alt', 'agl', 'vsi', 'hdg', 'status']);

interface Row {
  value: HTMLSpanElement;
}

export class FlightHud {
  readonly element: HTMLDivElement;
  private readonly rows = new Map<string, Row>();
  private nextUpdateMs = 0;
  private visibleValue = true;

  constructor(parent: HTMLElement = document.body) {
    this.element = document.createElement('div');
    this.element.className = 'hud';

    const title = document.createElement('div');
    title.className = 'hud__title';
    title.textContent = 'flight';
    this.element.append(title);

    this.addRow('ias', 'IAS');
    this.addRow('tas', 'TAS');
    this.addRow('alt', 'ALT');
    this.addRow('agl', 'AGL');
    this.addRow('vsi', 'VSI');
    this.addRow('hdg', 'HDG', true);
    this.addRow('pitch', 'PITCH');
    this.addRow('bank', 'BANK');
    this.addRow('aoa', 'AoA', true);
    this.addRow('slip', 'SLIP');
    this.addRow('g', 'G');
    this.addRow('throttle', 'THR', true);
    this.addRow('rpm', 'RPM');
    this.addRow('power', 'POWER');
    this.addRow('flaps', 'FLAPS', true);
    this.addRow('trim', 'TRIM');
    this.addRow('rtrim', 'R-TRIM');
    this.addRow('atrim', 'A-TRIM');
    this.addRow('coord', 'COORD');
    this.addRow('status', '');

    parent.append(this.element);
  }

  private addRow(key: string, label: string, startsGroup = false): void {
    const row = document.createElement('div');
    const classes = ['hud__row'];
    if (startsGroup) classes.push('hud__row--group');
    // Tagged at build time rather than toggled at display time: compactness is then one class on
    // the panel and a stylesheet rule, so switching it touches one element instead of twenty.
    if (!COMPACT_ROWS.has(key)) classes.push('hud__row--detail');
    row.className = classes.join(' ');

    const labelEl = document.createElement('span');
    labelEl.className = 'hud__label';
    labelEl.textContent = label;

    const valueEl = document.createElement('span');
    valueEl.className = 'hud__value';
    valueEl.textContent = '—';

    row.append(labelEl, valueEl);
    this.element.append(row);
    this.rows.set(key, { value: valueEl });
  }

  private set(key: string, text: string, severity: 'normal' | 'warn' | 'bad' = 'normal'): void {
    const row = this.rows.get(key);
    if (row === undefined) return;
    if (row.value.textContent !== text) row.value.textContent = text;
    const className = severity === 'normal' ? 'hud__value' : `hud__value hud__value--${severity}`;
    if (row.value.className !== className) row.value.className = className;
  }

  get visible(): boolean {
    return this.visibleValue;
  }

  toggle(): void {
    this.setVisible(!this.visibleValue);
  }

  setVisible(visible: boolean): void {
    this.visibleValue = visible;
    this.element.hidden = !visible;
    this.nextUpdateMs = 0;
  }

  /**
   * Show only {@link COMPACT_ROWS} — the touch tier's panel.
   *
   * The hidden rows are still *written* every refresh. That is deliberate: skipping them would
   * mean the compact panel and the full one take different code paths, so a formatting bug could
   * exist on one and not the other. Twenty `textContent` comparisons at 10 Hz is 200 string
   * compares a second against a budget measured in milliseconds, and `set` already skips the
   * assignment when the text has not changed.
   */
  setCompact(compact: boolean): void {
    this.element.className = compact ? 'hud hud--compact' : 'hud';
  }

  /** Called once per frame; refreshes at {@link REFRESH_HZ}. */
  updateFrame(
    nowMs: number,
    state: FlightState,
    extra: {
      onGround: boolean;
      stalled: boolean;
      crashed: boolean;
      /** Auto-coordination status, when there is an assist to report on. */
      coordination?: { enabled: boolean; output: number };
    },
  ): void {
    if (!this.visibleValue || nowMs < this.nextUpdateMs) return;
    this.nextUpdateMs = nowMs + 1000 / REFRESH_HZ;

    const indicatedKt = mpsToKnots(state.indicatedAirspeedMps);
    this.set('ias', `${indicatedKt.toFixed(0)} kt`, extra.stalled ? 'bad' : 'normal');
    this.set('tas', `${mpsToKnots(state.trueAirspeedMps).toFixed(0)} kt`);
    this.set('alt', `${metresToFeet(state.altitudeM).toFixed(0)} ft`);
    this.set('agl', `${metresToFeet(state.heightAboveGroundM).toFixed(0)} ft`);

    const verticalSpeedFpm = mpsToFpm(state.verticalSpeedMps);
    this.set('vsi', `${verticalSpeedFpm >= 0 ? '+' : ''}${verticalSpeedFpm.toFixed(0)} fpm`);

    const headingDeg = ((state.headingRad * RAD2DEG) % 360 + 360) % 360;
    this.set('hdg', `${headingDeg.toFixed(0).padStart(3, '0')}°`);
    this.set('pitch', `${(state.pitchRad * RAD2DEG).toFixed(1)}°`);
    this.set('bank', `${(state.rollRad * RAD2DEG).toFixed(1)}°`);

    // Angle of attack and sideslip are `atan2` of the relative wind. Below flying speed that
    // is dominated by whatever breeze is blowing, so the readout is suppressed rather than
    // showing a confident 170° while the aircraft is parked.
    const flying = state.trueAirspeedMps > MIN_MEANINGFUL_AIRSPEED_MPS;
    const alphaDeg = state.alphaRad * RAD2DEG;
    this.set(
      'aoa',
      flying ? `${alphaDeg.toFixed(1)}°` : '—',
      extra.stalled ? 'bad' : flying && alphaDeg > 12 ? 'warn' : 'normal',
    );
    this.set('slip', flying ? `${(state.betaRad * RAD2DEG).toFixed(1)}°` : '—');
    this.set(
      'g',
      `${state.loadFactor.toFixed(2)}`,
      Math.abs(state.loadFactor) > 3.8 ? 'bad' : Math.abs(state.loadFactor) > 2.5 ? 'warn' : 'normal',
    );

    // Throttle is the lever position, not the engine's answer to it — the two differ for a
    // second after every movement, and "what have I actually set?" is the question being asked.
    this.set('throttle', `${(state.throttle * 100).toFixed(0)}%`);
    this.set('rpm', `${state.rpm.toFixed(0)}`);
    this.set('power', `${(state.powerW / 745.7).toFixed(0)} hp`);
    this.set('flaps', `${state.flapAngleDeg.toFixed(0)}°`);
    this.set('trim', formatTrim(state.trim, 'up', 'dn'));
    this.set('rtrim', formatTrim(state.rudderTrim, 'R', 'L'));
    this.set('atrim', formatTrim(state.aileronTrim, 'R', 'L'));
    this.set(
      'coord',
      extra.coordination === undefined
        ? '—'
        : extra.coordination.enabled
          ? `auto ${formatAssist(extra.coordination.output)}`
          : 'manual',
      extra.coordination?.enabled === false ? 'warn' : 'normal',
    );

    this.set(
      'status',
      extra.crashed ? 'CRASHED — R to reset' : extra.stalled ? 'STALL' : extra.onGround ? 'on ground' : 'airborne',
      extra.crashed ? 'bad' : extra.stalled ? 'bad' : 'normal',
    );
  }

  dispose(): void {
    this.element.remove();
  }
}

function formatTrim(trim: number, positive: string, negative: string): string {
  if (Math.abs(trim) < 0.005) return 'neutral';
  return `${Math.abs(trim * 100).toFixed(0)}% ${trim > 0 ? positive : negative}`;
}

/** How much rudder the assist is holding, in the direction a pilot would name it. */
function formatAssist(output: number): string {
  if (Math.abs(output) < 0.01) return '·';
  return `${output > 0 ? 'R' : 'L'}${Math.abs(output * 100).toFixed(0)}`;
}
